import { Options, SeriesOptionsType } from 'highcharts';
import moment from 'moment';
import { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import {
  FinancialsTooltipKeys,
  ProjectFinancialsContractType,
  ProjectFinancialsZoomLevel,
} from 'api/project';
import { currencyRenderer } from 'components/atoms/currency-renderer';
import { ProjectFinancialsContext } from 'contexts/project-financials';
import {
  projectFinancialsBaseYAxisOptions,
  ProjectFinancialsLineChartColorMap,
} from '../utils/charts/common';
import { projectFinancialsLineChartOptions } from '../utils/charts/line';
import { minsToHours, percentageRenderer } from '../utils/common';
import { useProjectFinancialsLabels } from './labels';
import { useFinancialsTooltips } from './tooltips';

interface ReturnProps {
  budgetLineChartOptions: Options | undefined;
  budgetLineChartTooltipContent: ReactNode;
  hoursLineChartOptions: Options | undefined;
  budgetLineChartZoomLevel: ProjectFinancialsZoomLevel;
  onBudgetLineChartZoomLevelChange: (value: ProjectFinancialsZoomLevel) => void;
}

export const useProjectFinancialsLineCharts = (): ReturnProps => {
  const context = useContext(ProjectFinancialsContext);
  const {
    currencyCode,
    project: { dueDate: projectEndDate },
    projectFinancials: {
      lineChartData,
      projectFinancialDetails: { financialContractType },
    },
    projectFinancialsVisibilityConfig: { profitabilityData, allocationData },
    permissionsMap: {
      projectProfitability: { canRead: canViewProfitability },
    },
    revenueLabels,
    timeLabels,
    percentageLabels,
  } = context;

  const {
    todayLabel,
    projectBudgetLabel,
    revenueLabel,
    estimatedRevenueLabel,
    costLabel,
    estimatedCostLabel,
    profitLabel,
    estimatedProfitLabel,
    marginLabel,
    estimatedMarginLabel,
    projectHoursLabel,
    timeAllocatedLabel,
    timeTrackedLabel,
    billableTimeTrackedLabel,
    nonBillableTimeTrackedLabel,
  } = useProjectFinancialsLabels({ financialContractType });

  const { getTooltipContent } = useFinancialsTooltips({
    financialContractType,
  });

  const lastDateMarker = useMemo(
    () =>
      projectEndDate != null
        ? moment(projectEndDate).startOf('day')
        : undefined,
    [projectEndDate],
  );

  const getChartOptions = useMemo(
    () =>
      projectFinancialsLineChartOptions({
        lastDateMarker,
        currencyCode,
        todayLabel,
        revenueLabels,
        timeLabels,
        percentageLabels,
      }),
    [
      currencyCode,
      lastDateMarker,
      percentageLabels,
      revenueLabels,
      timeLabels,
      todayLabel,
    ],
  );

  const [budgetLineChartZoomLevel, setBudgetLineChartZoomLevel] =
    useState<ProjectFinancialsZoomLevel>(ProjectFinancialsZoomLevel.Revenue);

  const onBudgetLineChartZoomLevelChange = useCallback(
    (value: ProjectFinancialsZoomLevel) => {
      setBudgetLineChartZoomLevel(value);
    },
    [],
  );

  const budgetLineChartOptions: Options | undefined = useMemo(() => {
    if (
      lineChartData == null ||
      (financialContractType === ProjectFinancialsContractType.NONBILLABLE &&
        !profitabilityData)
    ) {
      return undefined;
    }

    const projectBudgetData: number[][] = [];
    const estimatedRevenueData: number[][] = [];
    const actualRevenueData: number[][] = [];
    const estimatedCostData: number[][] = [];
    const actualCostData: number[][] = [];
    const estimatedProfitData: number[][] = [];
    const actualProfitData: number[][] = [];
    const estimatedMarginData: number[][] = [];
    const actualMarginData: number[][] = [];

    lineChartData.forEach(
      ({
        startDate,
        projectBudget,
        estimatedRevenue,
        actualRevenue,
        estimatedCost,
        actualCost,
        estimatedProfit,
        actualProfit,
        estimatedMargin,
        actualMargin,
      }) => {
        const startDateMs = moment(startDate).startOf('day').utc().valueOf();

        if (projectBudget != null) {
          projectBudgetData.push([startDateMs, projectBudget]);
        }
        if (estimatedRevenue != null) {
          estimatedRevenueData.push([startDateMs, estimatedRevenue]);
        }
        if (actualRevenue != null) {
          actualRevenueData.push([startDateMs, actualRevenue]);
        }
        if (estimatedCost != null) {
          estimatedCostData.push([startDateMs, estimatedCost]);
        }
        if (actualCost != null) {
          actualCostData.push([startDateMs, actualCost]);
        }
        if (estimatedProfit != null) {
          estimatedProfitData.push([startDateMs, estimatedProfit]);
        }
        if (actualProfit != null) {
          actualProfitData.push([startDateMs, actualProfit]);
        }
        if (estimatedMargin != null) {
          estimatedMarginData.push([startDateMs, estimatedMargin]);
        }
        if (actualMargin != null) {
          actualMarginData.push([startDateMs, actualMargin]);
        }
      },
    );

    const budgetColumn: SeriesOptionsType = {
      type: 'spline',
      name: projectBudgetLabel,
      legendIndex: 0,
      yAxis: 0,
      color:
        ProjectFinancialsLineChartColorMap[financialContractType].projectBudget,
      dashStyle: 'Solid',
      data: projectBudgetData,
    };
    const estimatedRevenueColumn: SeriesOptionsType = {
      type: 'spline',
      name: estimatedRevenueLabel,
      legendIndex: 1,
      yAxis: 0,
      color:
        ProjectFinancialsLineChartColorMap[financialContractType]
          .estimatedRevenue,
      dashStyle: 'ShortDash',
      data: estimatedRevenueData,
    };
    const actualRevenueColumn: SeriesOptionsType = {
      type: 'spline',
      name: revenueLabel,
      legendIndex: 2,
      yAxis: 0,
      color:
        ProjectFinancialsLineChartColorMap[financialContractType].actualRevenue,
      dashStyle: 'Solid',
      data: actualRevenueData,
    };
    const estimatedCostColumn: SeriesOptionsType = {
      type: 'spline',
      name: estimatedCostLabel,
      legendIndex: 3,
      yAxis: 0,
      color:
        ProjectFinancialsLineChartColorMap[financialContractType].estimatedCost,
      dashStyle: 'ShortDash',
      data: estimatedCostData,
    };
    const actualCostColumn: SeriesOptionsType = {
      type: 'spline',
      name: costLabel,
      legendIndex: 4,
      yAxis: 0,
      color:
        ProjectFinancialsLineChartColorMap[financialContractType].actualCost,
      dashStyle: 'Solid',
      data: actualCostData,
    };
    const estimatedProfitColumn: SeriesOptionsType = {
      type: 'spline',
      name: estimatedProfitLabel,
      legendIndex: 5,
      yAxis: 0,
      color:
        ProjectFinancialsLineChartColorMap[financialContractType]
          .estimatedProfit,
      dashStyle: 'ShortDash',
      data: estimatedProfitData,
    };
    const actualProfitColumn: SeriesOptionsType = {
      type: 'spline',
      name: profitLabel,
      legendIndex: 6,
      yAxis: 0,
      color:
        ProjectFinancialsLineChartColorMap[financialContractType].actualProfit,
      dashStyle: 'Solid',
      data: actualProfitData,
    };
    const estimatedMarginColumn: SeriesOptionsType = {
      type: 'spline',
      name: estimatedMarginLabel,
      legendIndex: 7,
      yAxis: 1,
      color:
        ProjectFinancialsLineChartColorMap[financialContractType]
          .estimatedMargin,
      dashStyle: 'ShortDash',
      data: estimatedMarginData,
    };
    const actualMarginColumn: SeriesOptionsType = {
      type: 'spline',
      name: marginLabel,
      legendIndex: 8,
      yAxis: 1,
      color:
        ProjectFinancialsLineChartColorMap[financialContractType].actualMargin,
      dashStyle: 'Solid',
      data: actualMarginData,
    };

    const options: Options = {
      ...getChartOptions(),

      yAxis: [
        projectFinancialsBaseYAxisOptions({
          labels: {
            formatter: (obj) =>
              currencyRenderer(parseInt(obj.value, 10), currencyCode),
          },
        }),
        ...(budgetLineChartZoomLevel ===
        ProjectFinancialsZoomLevel.Profitability
          ? [
              projectFinancialsBaseYAxisOptions({
                opposite: true,
                labels: {
                  formatter: (obj) => percentageRenderer(obj.value),
                },
              }),
            ]
          : []),
      ],

      series: [
        ...(financialContractType === ProjectFinancialsContractType.NONBILLABLE
          ? [
              ...(canViewProfitability && profitabilityData
                ? [
                    ...(allocationData ? [estimatedCostColumn] : []),
                    actualCostColumn,
                  ]
                : []),
            ]
          : [
              ...(budgetLineChartZoomLevel ===
              ProjectFinancialsZoomLevel.Revenue
                ? [
                    budgetColumn,
                    ...(allocationData ? [estimatedRevenueColumn] : []),
                    actualRevenueColumn,
                  ]
                : [
                    ...(allocationData ? [estimatedRevenueColumn] : []),
                    actualRevenueColumn,
                    ...(canViewProfitability && profitabilityData
                      ? [
                          ...(allocationData ? [estimatedCostColumn] : []),
                          actualCostColumn,
                          ...(allocationData ? [estimatedProfitColumn] : []),
                          actualProfitColumn,
                          ...(allocationData ? [estimatedMarginColumn] : []),
                          actualMarginColumn,
                        ]
                      : []),
                  ]),
            ]),
      ],
    };

    return options;
  }, [
    lineChartData,
    projectBudgetLabel,
    financialContractType,
    estimatedRevenueLabel,
    revenueLabel,
    estimatedCostLabel,
    costLabel,
    estimatedProfitLabel,
    profitLabel,
    estimatedMarginLabel,
    marginLabel,
    getChartOptions,
    budgetLineChartZoomLevel,
    allocationData,
    canViewProfitability,
    profitabilityData,
    currencyCode,
  ]);

  const budgetLineChartTooltipContent = useMemo(
    () =>
      lineChartData == null ||
      (financialContractType === ProjectFinancialsContractType.NONBILLABLE &&
        !profitabilityData)
        ? undefined
        : getTooltipContent(
            [
              ...(financialContractType ===
              ProjectFinancialsContractType.NONBILLABLE
                ? [
                    ...(canViewProfitability && profitabilityData
                      ? ['cost']
                      : []),
                  ]
                : [
                    ...(budgetLineChartZoomLevel ===
                    ProjectFinancialsZoomLevel.Revenue
                      ? ['revenue']
                      : [
                          'revenue',
                          ...(canViewProfitability && profitabilityData
                            ? ['cost', 'profit', 'margin']
                            : []),
                        ]),
                  ]),
            ] as FinancialsTooltipKeys[],
            allocationData,
          ),
    [
      allocationData,
      budgetLineChartZoomLevel,
      canViewProfitability,
      financialContractType,
      getTooltipContent,
      lineChartData,
      profitabilityData,
    ],
  );

  const hoursLineChartOptions: Options | undefined = useMemo(() => {
    if (lineChartData == null) {
      return undefined;
    }

    const projectHoursData: number[][] = [];
    const timeAllocatedData: number[][] = [];
    const timeTrackedData: number[][] = [];
    const billableTimeTrackedData: number[][] = [];
    const nonBillableTimeTrackedData: number[][] = [];

    lineChartData.forEach(
      ({
        startDate,
        projectHours,
        timeAllocated,
        timeTracked,
        billableTimeTracked,
        nonBillableTimeTracked,
      }) => {
        const startDateMs = moment(startDate).startOf('day').utc().valueOf();

        if (projectHours != null) {
          projectHoursData.push([startDateMs, projectHours]);
        }
        timeAllocatedData.push([startDateMs, minsToHours(timeAllocated ?? 0)]);
        if (billableTimeTracked != null) {
          billableTimeTrackedData.push([
            startDateMs,
            minsToHours(billableTimeTracked),
          ]);
        }
        if (nonBillableTimeTracked != null) {
          nonBillableTimeTrackedData.push([
            startDateMs,
            minsToHours(nonBillableTimeTracked),
          ]);
        }
        if (timeTracked != null) {
          timeTrackedData.push([startDateMs, minsToHours(timeTracked)]);
        }
      },
    );

    const projectHoursColumn: SeriesOptionsType = {
      type: 'spline',
      name: projectHoursLabel,
      legendIndex: 0,
      color:
        ProjectFinancialsLineChartColorMap[financialContractType].projectHours,
      dashStyle: 'Solid',
      data: projectHoursData,
    };
    const timeAllocatedColumn: SeriesOptionsType = {
      type: 'spline',
      name: timeAllocatedLabel,
      legendIndex: 1,
      color:
        ProjectFinancialsLineChartColorMap[financialContractType].timeAllocated,
      dashStyle: 'ShortDash',
      data: timeAllocatedData,
    };
    const timeTrackedColumn: SeriesOptionsType = {
      type: 'spline',
      name: timeTrackedLabel,
      legendIndex: 2,
      color:
        ProjectFinancialsLineChartColorMap[financialContractType].timeTracked,
      dashStyle: 'Solid',
      data: timeTrackedData,
    };
    const billabledTimeTrackedColumn: SeriesOptionsType = {
      type: 'spline',
      name: billableTimeTrackedLabel,
      legendIndex: 3,
      color:
        ProjectFinancialsLineChartColorMap[financialContractType]
          .billableTimeTracked,
      dashStyle: 'Solid',
      data: billableTimeTrackedData,
    };
    const nonBillabledTimeTrackedColumn: SeriesOptionsType = {
      type: 'spline',
      name: nonBillableTimeTrackedLabel,
      legendIndex: 3,
      color:
        ProjectFinancialsLineChartColorMap[financialContractType]
          .nonBillableTimeTracked,
      dashStyle: 'Solid',
      data: nonBillableTimeTrackedData,
    };

    const options: Options = {
      ...getChartOptions(),

      yAxis: [
        projectFinancialsBaseYAxisOptions({
          min: 0,
          labels: {
            format: '{value}h',
          },
        }),
      ],

      series: [
        ...(financialContractType === ProjectFinancialsContractType.NONBILLABLE
          ? [
              ...(allocationData ? [timeAllocatedColumn] : []),
              nonBillabledTimeTrackedColumn,
            ]
          : [
              projectHoursColumn,
              ...(allocationData ? [timeAllocatedColumn] : []),
              timeTrackedColumn,
              billabledTimeTrackedColumn,
            ]),
      ],
    };

    return options;
  }, [
    allocationData,
    billableTimeTrackedLabel,
    financialContractType,
    getChartOptions,
    lineChartData,
    nonBillableTimeTrackedLabel,
    projectHoursLabel,
    timeAllocatedLabel,
    timeTrackedLabel,
  ]);

  return {
    budgetLineChartOptions,
    budgetLineChartTooltipContent,
    hoursLineChartOptions,
    budgetLineChartZoomLevel,
    onBudgetLineChartZoomLevelChange,
  };
};
